import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {ChevronDownIcon} from "@radix-ui/react-icons";
import {
	ArrowRight,
	Briefcase,
	Camera,
	Check,
	CheckCircle2,
	Globe,
	Loader2,
	MapPin,
	MessageCircle,
	Phone,
	RefreshCw,
	ShieldCheck,
	ShoppingBag,
	Store,
	X
} from 'lucide-react';
import {t} from "i18next";
import {toast} from "react-toastify";
// ① Country codes now imported from shared data file instead of being hardcoded
import {countryCodes} from "@/data/CountryCode";
import YellowPageListingCard from "@/components/YellowPageListingCard";
import {createEntity} from "@/service/yellowPageApi";
import {useAxiosPrivate} from "@/hooks/useAxiosPrivate";

// ─── Constants ────────────────────────────────────────────────────────────────
const FALLBACK_IMAGE = '/other_imgs/logo-joglekar kul-72x72.png';
const RESEND_COOLDOWN = 30;
// Multicategory: allow up to 3 selections
const MAX_CATEGORIES = 3;
const MAX_LEN_ENTITY_NAME = 60;
const MAX_LEN_SERVICE_AREA = 40;
const MAX_LEN_LOCATION = 50;
const MAX_LEN_WEBSITE = 100;


// ─── Utility Functions ────────────────────────────────────────────────────────────────
// Utility to get scrollbar width — add once near the top of the file
const getScrollbarWidth = (el) => el.offsetWidth - el.clientWidth;
const containsDevanagari = (str) => /[\u0900-\u097F]/.test(str);

const useDelayedClose = (isOpen, delay = 140) => {
	const [shouldRender, setShouldRender] = useState(isOpen);
	const [isClosing, setIsClosing] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setShouldRender(true);
			setIsClosing(false);
		} else if (shouldRender) {
			setIsClosing(true);
			const t = setTimeout(() => {
				setShouldRender(false);
				setIsClosing(false);
			}, delay);
			return () => clearTimeout(t);
		}
	}, [isOpen]);

	return {shouldRender, isClosing};
};

// Fallback list shown while the API call is in-flight / if it fails.
//    Remove once the API is wired in.
const FALLBACK_CATEGORIES = [
	"SHOPPING_RETAIL", "FOOD_BEVERAGES", "BEAUTY_HEALTH",
	"PROFESSIONAL_SERVICES", "EDUCATION_TRAINING", "EVENTS_LEISURE",
	"TECHNOLOGY_IT", "CONSTRUCTION_HOME", "OTHER",
];
const categoryColors = {
	"SHOPPING_RETAIL": "#E8722A", "FOOD_BEVERAGES": "#D97706",
	"BEAUTY_HEALTH": "#DB2777", "PROFESSIONAL_SERVICES": "#4F46E5",
	"EDUCATION_TRAINING": "#0891B2", "EVENTS_LEISURE": "#7C3AED",
	"TECHNOLOGY_IT": "#0284C7", "CONSTRUCTION_HOME": "#65A30D", "OTHER": "#6B7280",
};

// Translates a category key → active language label
// Falls back to the key itself if translation is missing
const getCategoryLabel = (key) => t(`YELLOW_PAGE.CATEGORIES.${key}`, {defaultValue: key});

// ⑥ Translation helper — keeps call-sites short
const tf = (key, opts) => t(`YELLOW_PAGE.ONBOARDING_FORM.${key}`, opts);

// Derive the default country object once (India, or first in list)
const DEFAULT_COUNTRY = countryCodes.find(c => c.countryCode === 'IN') ?? countryCodes[0];
const makePhoneState = (country = DEFAULT_COUNTRY) => ({
	charCode: country.countryCode,
	dialingCode: country.countryDialingCode,
	countryFlagSrc: country.countryFlagSrc,
	countryName: country.countryName,
	number: '',
});

// ─── StepIndicator ────────────────────────────────────────────────────────────
const StepIndicator = ({currentStep}) => {
	const steps = [
		{num: 1, label: tf('STEPS.TYPE')},
		{num: 2, label: tf('STEPS.DETAILS')},
		{num: 3, label: tf('STEPS.CONTACT')},
		{num: 4, label: tf('STEPS.FINISH')},
	];
	return (
		<div className="flex items-center w-full mt-3">
			{steps.map((step, i) => {
				const done = currentStep > step.num, active = currentStep === step.num;
				return (
					<React.Fragment key={step.num}>
						<div className="flex flex-col items-center gap-1">
							<div
								className="w-8 h-8 rounded-full flex items-center justify-center font-custom-bold text-xs transition-all duration-300"
								style={{
									background: done ? '#16A34A' : active ? '#FDF0ED' : '#F3F4F6',
									border: `2px solid ${done ? '#16A34A' : active ? '#D26A53' : '#E5E7EB'}`,
									color: done ? '#fff' : active ? '#D26A53' : '#9CA3AF',
									boxShadow: done ? '0 0 0 4px rgba(22,163,74,0.15)' : active ? '0 0 0 4px rgba(210,106,83,0.12)' : 'none',
								}}
							>
								{done ? <Check className="w-3.5 h-3.5"/> : step.num}
							</div>
							<span className="font-custom-semi-bold text-[0.6rem] tracking-wide"
							      style={{color: active ? '#D26A53' : done ? '#6B7280' : '#9CA3AF'}}>
								{step.label}
							</span>
						</div>
						{i < steps.length - 1 && (
							<div className="flex-1 mx-1.5 mb-4">
								<div className="h-0.5 w-full rounded-full overflow-hidden bg-gray-200">
									<div className="h-full rounded-full transition-all duration-500"
									     style={{
										     width: currentStep > step.num ? '100%' : '0%',
										     background: currentStep > step.num ? '#16A34A' : '#D26A53'
									     }}/>
								</div>
							</div>
						)}
					</React.Fragment>
				);
			})}
		</div>
	);
};

// ─── Shared form primitives ───────────────────────────────────────────────────
const Field = ({children, error, counter}) => (
	<div className="flex flex-col gap-1">
		{children}
		{(error || counter) && (
			<div className="flex items-start justify-between gap-2 mt-0.5">
				{error ? (
					<p className="custom-input-error-message flex items-center gap-1 flex-1 flex-wrap">
						<span className="w-1.5 h-1.5 bg-red-400 rounded-full inline-block shrink-0"/> {error}
					</p>
				) : <span/>}
				{counter && (
					<p className="font-custom-regular text-[0.6rem] text-gray-300 shrink-0">{counter}</p>
				)}
			</div>
		)}
	</div>
);

const InputLabel = ({htmlFor, children, required}) => (
	<label htmlFor={htmlFor} className="font-custom-semi-bold text-[0.75rem] text-gray-600 tracking-wide">
		{children}{required && <span className="text-red-400 ml-0.5">*</span>}
	</label>
);

const inputCls = (hasError) =>
	`w-full px-3.5 py-2.5 text-sm rounded-xl border outline-none transition-all duration-200 font-custom-semi-bold bg-white text-gray-800 placeholder-gray-300
	${hasError
		? 'border-red-300 ring-2 ring-red-100 focus:ring-red-200'
		: 'border-gray-200 focus:border-[#D26A53] focus:ring-2 focus:ring-[#D26A53]/10 hover:border-gray-300'}`;

// ─── PhoneRow — ④ keyboard-navigable country-code dropdown ────────────────────
const PhoneRow = ({
	                  value,
	                  onChange,
	                  countryCode,
	                  onCountryChange,
	                  error,
	                  placeholder = "9320638137",
	                  allCountryCodes
                  }) => {
	const [open, setOpen] = useState(false);
	const {shouldRender: showDrop, isClosing: dropClosing} = useDelayedClose(open);
	const [dropPos, setDropPos] = useState({top: 0, left: 0, width: 0});
	const [focusedIdx, setFocusedIdx] = useState(-1);
	const listRef = useRef(null);
	const btnRef = useRef(null);
	const containerRef = useRef(null);

	useEffect(() => {
		const scrollParent = btnRef.current?.closest('.overflow-y-auto');
		if (!scrollParent) return;
		if (open) {
			const sbWidth = getScrollbarWidth(scrollParent);
			const existingPadding = parseFloat(getComputedStyle(scrollParent).paddingRight) || 0;
			scrollParent.style.overflow = 'hidden';
			scrollParent.style.paddingRight = `${existingPadding + sbWidth}px`;
		} else {
			scrollParent.style.overflow = '';
			scrollParent.style.paddingRight = '';
		}
		return () => {
			scrollParent.style.overflow = '';
			scrollParent.style.paddingRight = '';
		};
	}, [open]);

	useEffect(() => {
		if (open && btnRef.current) {
			const r = btnRef.current.getBoundingClientRect();
			setDropPos({top: r.bottom + 4, left: r.left, width: r.width * 3});
			// width * 3 approximates the min-w-[11.25rem]; adjust to taste
		}
	}, [open]);

	useEffect(() => {
		if (focusedIdx >= 0 && listRef.current)
			listRef.current.children[focusedIdx]?.scrollIntoView({block: 'nearest'});
	}, [focusedIdx]);

	useEffect(() => {
		const handler = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target)) {
				closeDropdown();
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [open]);

	// ✅ CHANGED: returnFocus param — only Escape returns focus, Tab does not
	const closeDropdown = (returnFocus = false) => {
		setOpen(false);
		setFocusedIdx(-1);
		if (returnFocus) btnRef.current?.focus();
	};

	const handleKeyDown = (e) => {
		if (!open) {
			if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
				e.preventDefault();
				setOpen(true);
				setFocusedIdx(0);
			}
			return;
		}
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setFocusedIdx(i => Math.min(i + 1, allCountryCodes.length - 1));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setFocusedIdx(i => Math.max(i - 1, 0));
				break;
			case 'Enter':
			case ' ':
				e.preventDefault();
				if (focusedIdx >= 0) {
					onCountryChange(allCountryCodes[focusedIdx]);
					closeDropdown(true);
				}
				break;
			case 'Escape':
				e.preventDefault();
				closeDropdown(true);
				break;
			case 'Tab':
				closeDropdown();
				break;
		}
	};

	return (
		<Field error={error}>
			<div ref={containerRef} className="relative flex items-stretch">
				<button
					ref={btnRef} type="button"
					role="combobox" aria-expanded={open} aria-haspopup="listbox"
					onClick={() => {
						setOpen(v => !v);
						setFocusedIdx(open ? -1 : 0);
					}}
					onKeyDown={handleKeyDown}
					className="flex items-center gap-1.5 px-3 border border-r-0 rounded-l-xl bg-gray-50 hover:bg-gray-100 transition-colors shrink-0"
					style={{borderColor: error ? '#FCA5A5' : '#E5E7EB'}}>
					<img src={countryCode?.countryFlagSrc || allCountryCodes[0].countryFlagSrc} alt="flag"
					     className="w-4 h-3 rounded-sm object-cover"/>
					<span className="font-custom-bold text-[0.72rem] text-gray-700">
						{countryCode?.dialingCode || DEFAULT_COUNTRY.countryDialingCode}
					</span>
					<ChevronDownIcon
						className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}/>
				</button>

				<input type="number"
				       className={`flex-1 px-3.5 py-2.5 text-sm rounded-r-xl border outline-none transition-all duration-200 font-custom-semi-bold bg-white text-gray-800 placeholder-gray-300
					       ${error ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200 focus:border-[#D26A53] focus:ring-2 focus:ring-[#D26A53]/10'}`}
				       placeholder={placeholder} value={value}
				       onChange={e => onChange(e.target.value)}/>


				{showDrop && ReactDOM.createPortal(
					<div
						style={{
							position: 'fixed',
							top: dropPos.top,
							left: dropPos.left,
							width: dropPos.width,
							zIndex: 9999
						}}
						className={`${dropClosing ? 'dropdown-close' : 'dropdown-open'} overflow-hidden bg-white rounded-xl shadow-xl border border-gray-100`}>
						<div ref={listRef} role="listbox" onWheel={e => e.stopPropagation()}
						     className="overflow-y-auto max-h-32">
							{allCountryCodes.map((c, i) => (
								<button key={i} type="button" role="option"
								        aria-selected={countryCode?.charCode === c.countryCode}
								        className={`w-full flex items-center gap-2 px-3 py-2.5 font-custom-medium text-[0.75rem] transition-colors
								        ${focusedIdx === i ? 'bg-orange-50 text-[#D26A53]' : 'text-gray-700 hover:bg-gray-50'}`}
								        onMouseEnter={() => setFocusedIdx(i)}
								        onClick={() => {
									        onCountryChange(c);
									        closeDropdown(true);
								        }}>
									<img src={c.countryFlagSrc} alt={c.countryCode}
									     className="w-4 h-3 rounded-sm object-cover"/>
									{c.countryName} ({c.countryDialingCode})
								</button>
							))}
						</div>
					</div>,
					document.body
				)}
			</div>
		</Field>
	);
};

// ─── PreviewCard — ⑤ supports multiple categories ─────────────────────────────
const PreviewCard = ({formData, ownerName, nativePlace, ownerImageUrl, ownerImageUrlBg, updatedFields}) => (
	<div className="flex flex-col gap-2.5 w-full">
		<div className="flex items-center justify-between mb-0.5">
			<p className="font-custom-bold text-[0.6rem] text-gray-400 uppercase tracking-widest">
				{tf('PREVIEW.LABEL')}
			</p>
			<span
				className="flex items-center gap-1 font-custom-bold text-[0.55rem] text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse"/> {tf('PREVIEW.STATUS_ACTIVE')}
            </span>
		</div>

		<YellowPageListingCard
			entityType={formData.entityType}
			entityName={formData.entityName}
			tagline={formData.tagline}
			categories={formData.categories}
			logo={formData.logo}
			isVerified={formData.otpVerified}
			isPhysical={formData.isPhysical}
			location={formData.location}
			serviceArea={formData.serviceArea}
			phone={formData.phone}
			callingPhone={formData.callingPhone}
			sameNumberForCalling={formData.sameNumberForCalling}
			website={formData.website}
			primaryCta={formData.primaryCta}
			ownerName={ownerName}
			nativePlace={nativePlace}
			ownerImageUrl={ownerImageUrl}
			ownerImageUrlBg={ownerImageUrlBg}
			updatedFields={updatedFields}
			isPreview={true}
		/>

		<p className="font-custom-regular text-[0.6rem] text-gray-400 text-center leading-relaxed px-2">
			{tf('PREVIEW.FOOTER_NOTE')}
		</p>
	</div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const YellowPageOnboardingForm = ({setIsYellowPageOnboardingFormModalOpen, accountDetails}) => {
	const axiosPrivate = useAxiosPrivate();

	const [currentStep, setCurrentStep] = useState(1);
	const [errors, setErrors] = useState({});
	const [updatedFields, setUpdatedFields] = useState([]);
	// ① Track navigation direction for slide animation
	const stepDirectionRef = useRef(1); // 1 = forward, -1 = backward

	// ─── ② Categories from API ────────────────────────────────────────────────
	const [categoryList, setCategoryList] = useState(FALLBACK_CATEGORIES);
	const [categoriesLoading, setCategoriesLoading] = useState(false);

	useEffect(() => {
		const fetchCategories = async () => {
			setCategoriesLoading(true);
			try {
				// TODO: Wire to real API — replace the lines below:
				// const res  = await fetch('/api/yellow-pages/categories');
				// const data = await res.json();
				// setCategoryList(data.categories);
				setCategoryList(FALLBACK_CATEGORIES); // ← remove once API is integrated
			} catch (err) {
				console.error('[YellowPagesHomePage] Failed to load categories, using fallback:', err);
				setCategoryList(FALLBACK_CATEGORIES);
			} finally {
				setCategoriesLoading(false);
			}
		};
		fetchCategories();
	}, []);

	// ─── ④ Category dropdown + keyboard state ─────────────────────────────────
	const [isCatOpen, setIsCatOpen] = useState(false);
	const {shouldRender: showCat, isClosing: catClosing} = useDelayedClose(isCatOpen);
	const [catFocusedIdx, setCatFocusedIdx] = useState(-1);
	const catListRef = useRef(null);
	const catBtnRef = useRef(null);
	const catContainerRef = useRef(null);
	const [catDropPos, setCatDropPos] = useState({top: 0, left: 0, width: 0});

	// ─── OTP state ────────────────────────────────────────────────────────────
	const [otpSent, setOtpSent] = useState(false);
	const [otpValue, setOtpValue] = useState('');
	const [resendTimer, setResendTimer] = useState(0);
	const timerRef = useRef(null);

	const logoFileRef = useRef(null);

	// ─── Form data ────────────────────────────────────────────────────────────
	const [formData, setFormData] = useState({
		entityType: '',
		entityName: '',
		categories: [],
		phone: makePhoneState(),
		otpVerified: false,
		sameNumberForCalling: false,
		callingPhone: makePhoneState(),
		isPhysical: true, location: '', serviceArea: '',
		tagline: '', website: '', logo: '', termsAccepted: false,
		primaryCta: 'whatsapp', // default — options: 'whatsapp' | 'call' | 'website'
	});

	const ownerName = accountDetails?.firstName;
	const nativePlace = accountDetails?.familyTreeName;
	const ownerImageUrl = accountDetails?.imageUrl;
	const ownerImageUrlBg = accountDetails?.imageUrlBg;

	const [disabledCtaTooltip, setDisabledCtaTooltip] = useState(null); // stores which val is showing tooltip
	const disabledCtaTooltipRef = useRef(null);

	useEffect(() => {
		if (catFocusedIdx >= 0 && catListRef.current)
			catListRef.current.children[catFocusedIdx]?.scrollIntoView({block: 'nearest'});
	}, [catFocusedIdx]);

	useEffect(() => {
		if (isCatOpen && catBtnRef.current) {
			const r = catBtnRef.current.getBoundingClientRect();
			setCatDropPos({top: r.bottom + 4, left: r.left, width: r.width});
		}
	}, [isCatOpen, formData.categories]);

	useEffect(() => {
		const scrollParent = catBtnRef.current?.closest('.overflow-y-auto');
		if (!scrollParent) return;
		if (isCatOpen) {
			const sbWidth = getScrollbarWidth(scrollParent);
			const existingPadding = parseFloat(getComputedStyle(scrollParent).paddingRight) || 0;
			scrollParent.style.overflow = 'hidden';
			scrollParent.style.paddingRight = `${existingPadding + sbWidth}px`;
		} else {
			scrollParent.style.overflow = '';
			scrollParent.style.paddingRight = '';
		}
		return () => {
			scrollParent.style.overflow = '';
			scrollParent.style.paddingRight = '';
		};
	}, [isCatOpen]);

	useEffect(() => {
		const handler = (e) => {
			if (catContainerRef.current && !catContainerRef.current.contains(e.target)) {
				setIsCatOpen(false);
				setCatFocusedIdx(-1);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [isCatOpen]);

	const showDisabledCtaTooltip = (val) => {
		setDisabledCtaTooltip(val);
		if (disabledCtaTooltipRef.current) clearTimeout(disabledCtaTooltipRef.current);
		disabledCtaTooltipRef.current = setTimeout(() => setDisabledCtaTooltip(null), 2500);
	};

	// Cleanup in existing useEffect:
	useEffect(() => () => {
		clearInterval(timerRef.current);
		if (disabledCtaTooltipRef.current) clearTimeout(disabledCtaTooltipRef.current);
	}, []);

	// ─── Helpers ──────────────────────────────────────────────────────────────
	const startResendTimer = () => {
		setResendTimer(RESEND_COOLDOWN);
		clearInterval(timerRef.current);
		timerRef.current = setInterval(() => {
			setResendTimer(prev => {
				if (prev <= 1) {
					clearInterval(timerRef.current);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	};

	const flashField = (field) => {
		setUpdatedFields(prev => [...prev, field]);
		setTimeout(() => setUpdatedFields(prev => prev.filter(f => f !== field)), 500);
	};

	const updateFormData = (field, value) => {
		setFormData(prev => ({...prev, [field]: value}));
		if (errors[field]) setErrors(prev => ({...prev, [field]: ''}));
		flashField(field);
	};

	// ⑤ Toggle a category in/out of the multi-select array
	const toggleCategory = (cat) => {
		setFormData(prev => {
			if (prev.categories.includes(cat))
				return {...prev, categories: prev.categories.filter(c => c !== cat)};
			if (prev.categories.length >= MAX_CATEGORIES) {
				toast.warn(tf('STEP_2.CATEGORY.MAX_ERROR', {max: MAX_CATEGORIES}));
				return prev;
			}
			return {...prev, categories: [...prev.categories, cat]};
		});
		setErrors(prev => ({...prev, categories: ''}));
		flashField('categories');
	};

	// ④ Category dropdown keyboard handler (keeps dropdown open for multi-select)
	const handleCatKeyDown = (e) => {
		if (!isCatOpen) {
			if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
				e.preventDefault();
				setIsCatOpen(true);
				setCatFocusedIdx(0);
			}
			return;
		}
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setCatFocusedIdx(i => Math.min(i + 1, categoryList.length - 1));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setCatFocusedIdx(i => Math.max(i - 1, 0));
				break;
			case 'Enter':
			case ' ':
				e.preventDefault();
				if (catFocusedIdx >= 0) toggleCategory(categoryList[catFocusedIdx]);
				break; // intentionally keep open — user may want to pick more
			case 'Escape':
				e.preventDefault();
				setIsCatOpen(false);
				setCatFocusedIdx(-1);
				catBtnRef.current?.focus();
				break;
			case 'Tab':
				setIsCatOpen(false);
				setCatFocusedIdx(-1);
				break;
		}
	};

	// ─── OTP handlers ─────────────────────────────────────────────────────────
	const handleSendOtp = () => {
		const rx10 = /^\d{10}$/;
		if (!formData.phone.number) {
			setErrors(p => ({...p, phone: tf('STEP_3.WHATSAPP.ERROR_REQUIRED')}));
			return;
		}
		if (!rx10.test(formData.phone.number)) {
			setErrors(p => ({...p, phone: tf('STEP_3.WHATSAPP.ERROR_INVALID')}));
			return;
		}
		toast.info(tf('TOAST.OTP_SENT', {dialingCode: formData.phone.dialingCode, number: formData.phone.number}));
		setOtpSent(true);
		setOtpValue('');
		startResendTimer();
		setErrors(p => ({...p, otp: '', phone: ''}));
	};

	const handleVerifyOtp = () => {
		if (otpValue.length !== 6) {
			setErrors(p => ({...p, otp: tf('STEP_3.WA_SECTION.ERROR_LENGTH')}));
			return;
		}
		setFormData(p => ({...p, otpVerified: true}));
		flashField('otpVerified');
		toast.success(tf('TOAST.VERIFIED'));
		setErrors(p => ({...p, otp: ''}));
	};

	const handleLogoUpload = (e) => {
		const file = e.target.files[0];
		if (file && file.type.startsWith('image/') && file.size <= 2 * 1024 * 1024) {
			logoFileRef.current = file;
			const reader = new FileReader();
			reader.onload = (ev) => updateFormData('logo', ev.target.result);
			reader.readAsDataURL(file);
		} else {
			toast.error(tf('TOAST.LOGO_ERROR'));
		}
	};

	// ─── Validation ───────────────────────────────────────────────────────────
	const validateStep = () => {
		let errs = {};

		if (currentStep === 1) {
			if (!formData.entityType)
				errs.entityType = tf('STEP_1.ERROR');
			if (!formData.categories.length)
				errs.categories = tf('STEP_2.CATEGORY.ERROR');
		}

		if (currentStep === 2) {
			if (!formData.entityName.trim()) errs.entityName = tf('STEP_2.ENTITY_NAME.ERROR');

			if (!errs.entityName && containsDevanagari(formData.entityName))
				errs.entityName = tf('ERRORS.DEVANAGARI_NOT_ALLOWED');

			if (formData.isPhysical) {
				if (!formData.location.trim()) errs.location = tf('STEP_2.LOCATION.ERROR_REQUIRED');
				else if (containsDevanagari(formData.location)) errs.location = tf('ERRORS.DEVANAGARI_NOT_ALLOWED');
			} else {
				if (!formData.serviceArea.trim()) errs.serviceArea = tf('STEP_2.SERVICE_AREA.ERROR');

				if (!errs.serviceArea && containsDevanagari(formData.serviceArea))
					errs.serviceArea = tf('ERRORS.DEVANAGARI_NOT_ALLOWED');
			}
		}

		if (currentStep === 3) {
			// ── WhatsApp moved here from Step 2 ──
			const rx10 = /^\d{10}$/;
			if (!formData.phone.number) errs.phone = tf('STEP_2.WHATSAPP.ERROR_REQUIRED');
			else if (!rx10.test(formData.phone.number)) errs.phone = tf('STEP_2.WHATSAPP.ERROR_INVALID');

			if (!formData.otpVerified) errs.otp = tf('STEP_3.WA_SECTION.ERROR_UNVERIFIED');

			if (!formData.sameNumberForCalling) {
				if (!formData.callingPhone.number) errs.callingPhone = tf('STEP_3.CALLING.ERROR_REQUIRED');
				else if (!rx10.test(formData.callingPhone.number)) errs.callingPhone = tf('STEP_3.CALLING.ERROR_INVALID');
			}

			// ── Website moved here from Step 4 ──
			if (containsDevanagari(formData.website))
				errs.website = tf('ERRORS.DEVANAGARI_NOT_ALLOWED');
		}

		if (currentStep === 4) {
			if (containsDevanagari(formData.tagline))
				errs.tagline = tf('ERRORS.DEVANAGARI_NOT_ALLOWED');

			if (!formData.termsAccepted)
				errs.termsAccepted = tf('STEP_4.TERMS.ERROR');
		}

		setErrors(errs);
		return Object.keys(errs).length === 0;
	};

	// ① Direction-tracking navigation
	const nextStep = () => {
		if (validateStep() && currentStep < 4) {
			stepDirectionRef.current = 1;
			setCurrentStep(p => p + 1);
		}
	};
	const prevStep = () => {
		if (currentStep > 1) {
			stepDirectionRef.current = -1;
			setCurrentStep(p => p - 1);
			setErrors({});
		}
	};

	const handleSubmit = async () => {
		if (!validateStep()) return;

		try {
			// Strip logo base64 from what we send — it goes as a file
			const {logo, ...formDataWithoutLogo} = formData;

			const response = await createEntity(axiosPrivate, formDataWithoutLogo, logoFileRef.current);

			if (response?.success) {
				toast.success(tf('TOAST.SUCCESS'));
				setTimeout(() => setIsYellowPageOnboardingFormModalOpen(false), 1500);
			} else {
				toast.error(response?.message || tf('TOAST.ERROR'));
			}
		} catch (error) {
			console.error("(YellowPageOnboardingForm) handleSubmit Error: ", error);
			toast.error(tf('TOAST.ERROR'));
		}
	};

	// ─── Step 1 — Type ────────────────────────────────────────────────────────
	const entityTypes = [
		{id: 'product', icon: Store, titleKey: 'STEP_1.PRODUCT.TITLE', descKey: 'STEP_1.PRODUCT.DESC'},
		{id: 'service', icon: Briefcase, titleKey: 'STEP_1.SERVICE.TITLE', descKey: 'STEP_1.SERVICE.DESC'},
		{id: 'hybrid', icon: ShoppingBag, titleKey: 'STEP_1.HYBRID.TITLE', descKey: 'STEP_1.HYBRID.DESC'},
	];

	// ④ Arrow-key navigation inside the entity-type radio group
	const handleEntityTypeKeyDown = (e) => {
		const ids = entityTypes.map(et => et.id);
		const cur = Math.max(ids.indexOf(formData.entityType), 0);
		if (['ArrowDown', 'ArrowRight'].includes(e.key)) {
			e.preventDefault();
			updateFormData('entityType', ids[(cur + 1) % ids.length]);
		}
		if (['ArrowUp', 'ArrowLeft'].includes(e.key)) {
			e.preventDefault();
			updateFormData('entityType', ids[(cur - 1 + ids.length) % ids.length]);
		}
	};

	const renderStep1 = () => (
		<div className="flex flex-col gap-4">
			<div>
				<h3 className="font-custom-extra-bold text-base text-gray-900">{tf('STEP_1.TITLE')}</h3>
				<p className="font-custom-regular text-[0.75rem] text-gray-500 mt-0.5">{tf('STEP_1.SUBTITLE')}</p>
			</div>
			<div className="flex flex-col gap-3 mt-1" role="radiogroup" aria-label={tf('STEP_1.TITLE')}>
				{entityTypes.map(type => {
					const active = formData.entityType === type.id;
					return (
						<button key={type.id}
						        role="radio" aria-checked={active}
						        onClick={() => updateFormData('entityType', type.id)}
						        onKeyDown={handleEntityTypeKeyDown}
						        className="flex items-center gap-4 p-4 rounded-xl text-left w-full transition-all duration-200"
						        style={{
							        border: `2px solid ${active ? '#D26A53' : '#E5E7EB'}`,
							        background: active ? '#FDF5F3' : '#FAFAFA',
							        boxShadow: active ? '0 2px 12px rgba(210,106,83,0.12)' : 'none',
						        }}>
							<div
								className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-200"
								style={{
									background: active ? '#D26A53' : '#F3F4F6',
									color: active ? '#fff' : '#6B7280'
								}}>
								<type.icon className="w-4.5 h-4.5"/>
							</div>
							<div className="flex-1 min-w-0">
								<p className="font-custom-bold text-sm text-gray-900">{tf(type.titleKey)}</p>
								<p className="font-custom-regular text-[0.68rem] text-gray-400 mt-0.5">{tf(type.descKey)}</p>
							</div>
							<div
								className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all duration-200"
								style={{
									background: active ? '#D26A53' : '#F3F4F6',
									border: `2px solid ${active ? '#D26A53' : '#E5E7EB'}`
								}}>
								{active && <Check className="w-3 h-3 text-white" strokeWidth={3}/>}
							</div>
						</button>
					);
				})}
			</div>
			{errors.entityType && (
				<p className="custom-input-error-message flex items-center gap-1">
					<span className="w-1.5 h-1.5 bg-red-400 rounded-full shrink-0"/> {errors.entityType}
				</p>
			)}

			{/* ⑤ Multi-select category field with keyboard nav */}
			<Field error={errors.categories}>
				<InputLabel required>{tf('STEP_2.CATEGORY.LABEL')}</InputLabel>

				{/* Selected category chips */}
				{formData.categories.length > 0 && (
					<div className="flex flex-wrap gap-1.5 mb-1">
						{formData.categories.map(cat => (
							<span key={cat}
							      className="flex items-center gap-1 px-2 py-0.5 rounded-md font-custom-bold text-[0.62rem]"
							      style={{
								      background: `${categoryColors[cat]}14`,
								      color: categoryColors[cat],
								      border: `1px solid ${categoryColors[cat]}30`
							      }}>
								{getCategoryLabel(cat)}
								<button type="button" onClick={() => toggleCategory(cat)}
								        className="hover:opacity-70 focus:outline-none ml-0.5"
								        aria-label={`Remove ${cat}`}>
									<X className="w-2.5 h-2.5"/>
								</button>
							</span>
						))}
					</div>
				)}

				<div ref={catContainerRef} className="relative">
					<button ref={catBtnRef} type="button"
					        role="combobox" aria-expanded={isCatOpen} aria-haspopup="listbox"
					        onClick={() => {
						        setIsCatOpen(v => !v);
						        setCatFocusedIdx(isCatOpen ? -1 : 0);
					        }}
					        onKeyDown={handleCatKeyDown}
					        className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border font-custom-semi-bold text-sm transition-all duration-200 bg-white"
					        style={{
						        borderColor: isCatOpen ? '#D26A53' : errors.categories ? '#FCA5A5' : '#E5E7EB',
						        boxShadow: isCatOpen ? '0 0 0 3px rgba(210,106,83,0.10)' : errors.categories ? '0 0 0 3px rgba(252,165,165,0.15)' : 'none',
						        color: formData.categories.length ? '#1F2937' : '#D1D5DB',
					        }}>
						<span>
							{categoriesLoading
								? tf('STEP_2.CATEGORY.LOADING')
								: formData.categories.length
									? tf('STEP_2.CATEGORY.SELECTED_COUNT', {
										count: formData.categories.length,
										max: MAX_CATEGORIES
									})
									: tf('STEP_2.CATEGORY.PLACEHOLDER', {max: MAX_CATEGORIES})}
						</span>
						{categoriesLoading
							? <Loader2 className="w-4 h-4 text-gray-400 animate-spin"/>
							: <ChevronDownIcon
								className={`transition-transform duration-200 text-gray-400 ${isCatOpen ? 'rotate-180' : ''}`}/>}
					</button>

					{showCat && ReactDOM.createPortal(
						<div
							style={{
								position: 'fixed',
								top: catDropPos.top,
								left: catDropPos.left,
								width: catDropPos.width,
								zIndex: 9999
							}}
							className={`${catClosing ? 'dropdown-close' : 'dropdown-open'} bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden`}
						>
							<div ref={catListRef} role="listbox" aria-multiselectable="true"
							     onWheel={e => e.stopPropagation()}
							     className="overflow-y-auto max-h-36">
								{categoryList.map((cat, i) => {
									const selected = formData.categories.includes(cat);
									return (
										<button key={i} type="button" role="option" aria-selected={selected}
										        className={`w-full text-left px-3.5 py-2.5 font-custom-medium text-[0.78rem] transition-colors flex items-center justify-between
										        ${catFocusedIdx === i ? 'bg-orange-50' : 'hover:bg-gray-50'}
										        ${selected ? 'text-[#D26A53] font-custom-semi-bold' : 'text-gray-700'}`}
										        onMouseEnter={() => setCatFocusedIdx(i)}
										        onClick={() => {
											        toggleCategory(cat);
										        }}>
											{getCategoryLabel(cat)}
											{selected && <Check className="w-3.5 h-3.5 text-[#D26A53] shrink-0"/>}
										</button>
									);
								})}
							</div>
						</div>,
						document.body
					)}
				</div>
				<p className="font-custom-regular text-[0.62rem] text-gray-400 mt-0.5">
					{tf('STEP_2.CATEGORY.MAX_HINT', {max: MAX_CATEGORIES})}
				</p>
			</Field>
		</div>
	);

	// ─── Step 2 — Details ─────────────────────────────────────────────────────
	const renderStep2 = () => (
		<div className="flex flex-col gap-4">
			<div>
				<h3 className="font-custom-extra-bold text-base text-gray-900">{tf('STEP_2.TITLE')}</h3>
				<p className="font-custom-regular text-[0.75rem] text-gray-500 mt-0.5">{tf('STEP_2.SUBTITLE')}</p>
			</div>

			<Field error={errors.entityName} counter={`${formData.entityName.length}/${MAX_LEN_ENTITY_NAME}`}>
				<InputLabel htmlFor="entityName" required>
					{tf('STEP_2.ENTITY_NAME.LABEL')}
				</InputLabel>
				<p className="font-custom-regular text-[0.62rem] text-amber-600 -mt-0.5">
					(Please enter in English only &nbsp;/&nbsp; कृपया इंग्रजीत लिहा)
				</p>
				<input
					id="entityName"
					className={inputCls(!!errors.entityName)}
					maxLength={MAX_LEN_ENTITY_NAME}
					placeholder={tf('STEP_2.ENTITY_NAME.PLACEHOLDER')} value={formData.entityName}
					onChange={e => updateFormData('entityName', e.target.value)}
					onBlur={() => {
						if (containsDevanagari(formData.entityName))
							setErrors(p => ({...p, entityName: tf('ERRORS.DEVANAGARI_NOT_ALLOWED')}));
					}}
				/>
			</Field>

			<div className="flex flex-col gap-1.5">
				<InputLabel>
					{tf('STEP_4.LOGO.LABEL')} <span
					className="font-custom-regular text-gray-400">{tf('STEP_4.LOGO.OPTIONAL')}</span>
				</InputLabel>
				<div className="relative rounded-xl overflow-hidden transition-all duration-200"
				     style={{border: '2px dashed #E5E7EB', background: '#FAFAFA'}}>
					<input type="file" accept="image/*" onChange={handleLogoUpload}
					       className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"/>
					{formData.logo ? (
						<div className="flex items-center gap-4 p-4">
							<img src={formData.logo} alt="Logo preview"
							     className="w-14 h-14 object-contain rounded-xl border border-gray-200 shadow-sm shrink-0"/>
							<div className="flex-1 min-w-0">
								<p className="font-custom-bold text-[0.78rem] text-gray-700">{tf('STEP_4.LOGO.UPLOADED_TITLE')}</p>
								<p className="font-custom-regular text-[0.65rem] text-gray-400 mt-0.5">{tf('STEP_4.LOGO.UPLOADED_HINT')}</p>
							</div>
							<button
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									updateFormData('logo', '');
									logoFileRef.current = null;
								}}
								className="relative z-20 w-7 h-7 rounded-full flex items-center justify-center
									shrink-0 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors"
							>
								<X className="w-3.5 h-3.5 text-red-500"/>
							</button>
						</div>
					) : (
						<div className="flex flex-col items-center justify-center py-7 gap-1.5">
							<div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-1">
								<Camera className="w-5 h-5 text-gray-400"/>
							</div>
							<p className="font-custom-semi-bold text-[0.78rem] text-gray-600">{tf('STEP_4.LOGO.UPLOAD_TITLE')}</p>
							<p className="font-custom-regular text-[0.6rem] text-gray-400">{tf('STEP_4.LOGO.UPLOAD_HINT')}</p>
						</div>
					)}
				</div>
			</div>

			{/* ④ Physical/Online — role="radiogroup" with arrow-key support */}
			<div className="flex flex-col gap-2">
				<InputLabel required>{tf('STEP_2.SERVE_TYPE.LABEL')}</InputLabel>
				<div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label={tf('STEP_2.SERVE_TYPE.LABEL')}>
					{[
						{
							val: true,
							icon: MapPin,
							labelKey: 'STEP_2.SERVE_TYPE.PHYSICAL',
							on: {background: '#EFF6FF', border: '2px solid #93C5FD', color: '#1D4ED8'}
						},
						{
							val: false,
							icon: Globe,
							labelKey: 'STEP_2.SERVE_TYPE.ONLINE',
							on: {background: '#F0FDF4', border: '2px solid #86EFAC', color: '#15803D'}
						},
					].map(({val, icon: Icon, labelKey, on}, idx, arr) => (
						<button key={String(val)}
						        role="radio" aria-checked={formData.isPhysical === val}
						        onClick={() => updateFormData('isPhysical', val)}
						        onKeyDown={e => {
							        if (['ArrowRight', 'ArrowDown'].includes(e.key)) {
								        e.preventDefault();
								        updateFormData('isPhysical', arr[(idx + 1) % arr.length].val);
							        }
							        if (['ArrowLeft', 'ArrowUp'].includes(e.key)) {
								        e.preventDefault();
								        updateFormData('isPhysical', arr[(idx - 1 + arr.length) % arr.length].val);
							        }
						        }}
						        className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl font-custom-bold text-[0.72rem] transition-all duration-200"
						        style={formData.isPhysical === val ? on : {
							        background: '#FAFAFA',
							        border: '2px solid #E5E7EB',
							        color: '#6B7280'
						        }}>
							<Icon className="w-3.5 h-3.5"/> {tf(labelKey)}
						</button>
					))}
				</div>
			</div>

			{formData.isPhysical ? (
				<Field error={errors.location} counter={`${formData.location.length}/${MAX_LEN_LOCATION}`}>
					<InputLabel htmlFor="location" required>
						{tf('STEP_2.LOCATION.LABEL')}
					</InputLabel>
					<p className="font-custom-regular text-[0.62rem] text-amber-600 -mt-0.5">
						Please enter in English only &nbsp;/&nbsp; कृपया इंग्रजीत लिहा
					</p>
					<input
						id="location" type="text" className={inputCls(!!errors.location)}
						placeholder={tf('STEP_2.LOCATION.PLACEHOLDER')} value={formData.location}
						maxLength={MAX_LEN_LOCATION}
						onChange={e => updateFormData('location', e.target.value)}
						onBlur={() => {
							if (containsDevanagari(formData.location))
								setErrors(p => ({...p, location: tf('ERRORS.DEVANAGARI_NOT_ALLOWED')}));
						}}
					/>
				</Field>
			) : (
				<Field error={errors.serviceArea} counter={`${formData.serviceArea.length}/${MAX_LEN_SERVICE_AREA}`}>
					<InputLabel htmlFor="serviceArea" required>
						{tf('STEP_2.SERVICE_AREA.LABEL')}
					</InputLabel>
					<p className="font-custom-regular text-[0.62rem] text-amber-600 -mt-0.5">
						(Please enter in English only &nbsp;/&nbsp; कृपया इंग्रजीत लिहा)
					</p>
					<input
						id="serviceArea" className={inputCls(!!errors.serviceArea)}
						placeholder={tf('STEP_2.SERVICE_AREA.PLACEHOLDER')} value={formData.serviceArea}
						maxLength={MAX_LEN_SERVICE_AREA}
						onChange={e => updateFormData('serviceArea', e.target.value)}
						onBlur={() => {
							if (containsDevanagari(formData.serviceArea))
								setErrors(p => ({...p, serviceArea: tf('ERRORS.DEVANAGARI_NOT_ALLOWED')}));
						}}
					/>
				</Field>
			)}
		</div>
	);

	// ─── Step 3 — Verify ──────────────────────────────────────────────────────
	const renderStep3 = () => (
		<div className="flex flex-col gap-5">
			<div>
				<h3 className="font-custom-extra-bold text-base text-gray-900">{tf('STEP_3.TITLE')}</h3>
				<p className="font-custom-regular text-[0.75rem] text-gray-500 mt-0.5">{tf('STEP_3.SUBTITLE')}</p>
			</div>

			{/* ── WhatsApp number ──────────────────────────────── */}
			<div className="flex flex-col gap-1.5">
				<InputLabel required>{tf('STEP_3.WHATSAPP.LABEL')}</InputLabel>
				<p className="font-custom-regular text-[0.65rem] text-gray-400 -mt-0.5">{tf('STEP_3.WHATSAPP.HINT')}</p>
				<PhoneRow
					value={formData.phone.number}
					countryCode={formData.phone}
					error={errors.phone}
					allCountryCodes={countryCodes}
					placeholder={tf('STEP_3.WHATSAPP.PLACEHOLDER')}
					onChange={v => {
						setFormData(p => ({...p, phone: {...p.phone, number: v}, otpVerified: false}));
						setErrors(p => ({...p, phone: ''}));
						flashField('phone');
					}}
					onCountryChange={c => setFormData(p => ({
						...p,
						phone: {
							...p.phone,
							dialingCode: c.countryDialingCode,
							countryFlagSrc: c.countryFlagSrc,
							charCode: c.countryCode,
							countryName: c.countryName
						},
					}))}
				/>
			</div>

			{/* ── OTP Verification card ─────────────────────────── */}
			<div className="rounded-2xl overflow-hidden" style={{border: '1.5px solid #E5E7EB'}}>
				<div className="px-4 py-3 flex items-center gap-3"
				     style={{background: '#F9FAFB', borderBottom: '1px solid #F0F0F0'}}>
					<div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
					     style={{background: '#DCFCE7', border: '1px solid #BBF7D0'}}>
						<MessageCircle className="w-4 h-4 text-green-600"/>
					</div>
					<div className="flex-1 min-w-0">
						<p className="font-custom-bold text-[0.78rem] text-gray-800">{tf('STEP_3.WA_SECTION.TITLE')}</p>
						<p className="font-custom-regular text-[0.65rem] text-gray-500 truncate">
							{tf('STEP_3.WA_SECTION.HINT')} <span
							className="font-custom-bold text-gray-700">{formData.phone.dialingCode} {formData.phone.number}</span>
						</p>
					</div>
					{formData.otpVerified && (
						<div
							className="flex items-center gap-1 font-custom-bold text-[0.6rem] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
							<ShieldCheck className="w-3 h-3"/> {tf('STEP_3.WA_SECTION.VERIFIED_BADGE')}
						</div>
					)}
				</div>

				<div className="p-4 flex flex-col gap-3">
					{!formData.otpVerified ? (
						<>
							{!otpSent ? (
								<button onClick={handleSendOtp}
								        className="w-full py-2.5 rounded-xl font-custom-bold text-sm text-white transition-all duration-150 hover:opacity-90 active:scale-95 flex items-center justify-center gap-2"
								        style={{
									        background: 'linear-gradient(135deg, #25D366, #1DA851)',
									        boxShadow: '0 4px 12px rgba(37,211,102,0.3)'
								        }}>
									<MessageCircle className="w-4 h-4"/> {tf('STEP_3.WA_SECTION.SEND_BTN')}
								</button>
							) : (
								<>
									<div className="flex gap-2 items-center">
										<input
											className={inputCls(!!errors.otp)}
											placeholder={tf('STEP_3.WA_SECTION.OTP_PLACEHOLDER')}
											maxLength={6} value={otpValue}
											autoFocus
											onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
											onChange={e => {
												setOtpValue(e.target.value.replace(/\D/g, ''));
												setErrors(p => ({...p, otp: ''}));
											}}
											style={{letterSpacing: '0.2em'}}/>
										<button onClick={handleVerifyOtp}
										        className="shrink-0 px-4 py-2.5 rounded-xl font-custom-bold text-sm text-white transition-all duration-150 hover:opacity-90 active:scale-95 whitespace-nowrap"
										        style={{
											        background: 'linear-gradient(135deg, #D26A53, #C05A43)',
											        boxShadow: '0 4px 12px rgba(210,106,83,0.3)'
										        }}>
											{tf('STEP_3.WA_SECTION.VERIFY_BTN')}
										</button>
									</div>
									{errors.otp && (
										<p className="custom-input-error-message flex items-center gap-1">
											<span
												className="w-1.5 h-1.5 bg-red-400 rounded-full shrink-0"/> {errors.otp}
										</p>
									)}
									<div className="flex items-center gap-1.5">
										{resendTimer > 0 ? (
											<p className="font-custom-regular text-[0.65rem] text-gray-400">
												{tf('STEP_3.WA_SECTION.RESEND_TIMER', {seconds: resendTimer})}
											</p>
										) : (
											<button onClick={handleSendOtp}
											        className="flex items-center gap-1 font-custom-semi-bold text-[0.65rem] text-[#D26A53] hover:underline transition-colors">
												<RefreshCw className="w-3 h-3"/> {tf('STEP_3.WA_SECTION.RESEND_BTN')}
											</button>
										)}
									</div>
								</>
							)}
						</>
					) : (
						<div className="flex items-center gap-2 py-2">
							<CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0"/>
							<p className="font-custom-semi-bold text-[0.78rem] text-emerald-700">{tf('STEP_3.WA_SECTION.SUCCESS_MSG')}</p>
						</div>
					)}
					{errors.otp && !otpSent && (
						<p className="custom-input-error-message flex items-center gap-1">
							<span className="w-1.5 h-1.5 bg-red-400 rounded-full shrink-0"/> {errors.otp}
						</p>
					)}
				</div>
			</div>

			{/* ── Calling number ───────────────────────────────── */}
			<div className="flex flex-col gap-3">
				<div className="flex items-start gap-2">
					<div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
					     style={{background: '#EFF6FF', border: '1px solid #BFDBFE'}}>
						<Phone className="w-4 h-4 text-blue-600"/>
					</div>
					<div>
						<p className="font-custom-bold text-[0.82rem] text-gray-800">{tf('STEP_3.CALLING.TITLE')}</p>
						<p className="font-custom-regular text-[0.65rem] text-gray-400 mt-0.5">{tf('STEP_3.CALLING.SUBTITLE')}</p>
					</div>
				</div>
				<div role="checkbox" tabIndex={0} aria-checked={formData.sameNumberForCalling}
				     className="flex gap-3 items-center p-3.5 rounded-xl cursor-pointer transition-all duration-200"
				     style={{
					     background: formData.sameNumberForCalling ? '#F0FDF4' : '#FAFAFA',
					     border: `1.5px solid ${formData.sameNumberForCalling ? '#86EFAC' : '#E5E7EB'}`
				     }}
				     onClick={() => updateFormData('sameNumberForCalling', !formData.sameNumberForCalling)}
				     onKeyDown={e => {
					     if (e.key === ' ' || e.key === 'Enter') {
						     e.preventDefault();
						     updateFormData('sameNumberForCalling', !formData.sameNumberForCalling);
					     }
				     }}>
					<div
						className="w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-all duration-200"
						style={{
							background: formData.sameNumberForCalling ? '#15803D' : '#fff',
							border: `2px solid ${formData.sameNumberForCalling ? '#15803D' : '#D1D5DB'}`
						}}>
						{formData.sameNumberForCalling && <Check className="w-3 h-3 text-white" strokeWidth={3}/>}
					</div>
					<p className="font-custom-medium text-[0.75rem] text-gray-700 select-none leading-snug">
						{tf('STEP_3.CALLING.SAME_AS_WA')}
						<span
							className="font-custom-regular text-gray-400 ml-1">({formData.phone.dialingCode} {formData.phone.number || '——'})</span>
					</p>
				</div>
				{!formData.sameNumberForCalling && (
					<div className="flex flex-col gap-1.5">
						<InputLabel required>{tf('STEP_3.CALLING.SEPARATE_LABEL')}</InputLabel>
						<PhoneRow
							value={formData.callingPhone.number}
							countryCode={formData.callingPhone}
							error={errors.callingPhone}
							allCountryCodes={countryCodes}
							onChange={v => {
								setFormData(p => ({...p, callingPhone: {...p.callingPhone, number: v}}));
								setErrors(p => ({...p, callingPhone: ''}));
							}}
							onCountryChange={c => setFormData(p => ({
								...p,
								callingPhone: {
									...p.callingPhone,
									dialingCode: c.countryDialingCode,
									countryFlagSrc: c.countryFlagSrc,
									charCode: c.countryCode,
									countryName: c.countryName
								}
							}))}
						/>
					</div>
				)}
			</div>

			{/* ── Website ──────────────────────────────────────── */}
			<Field error={errors.website}>
				<InputLabel htmlFor="website">
					{tf('STEP_3.WEBSITE.LABEL')} <span
					className="font-custom-regular text-gray-400">{tf('STEP_3.WEBSITE.OPTIONAL')}</span>
				</InputLabel>
				<p className="font-custom-regular text-[0.62rem] text-amber-600 -mt-0.5">
					Please enter in English only &nbsp;/&nbsp; कृपया इंग्रजीत लिहा
				</p>
				<input
					id="website" className={inputCls(!!errors.website)}
					placeholder={tf('STEP_3.WEBSITE.PLACEHOLDER')}
					maxLength={MAX_LEN_WEBSITE}
					value={formData.website}
					onChange={e => updateFormData('website', e.target.value)}
					onBlur={() => {
						if (containsDevanagari(formData.website)) setErrors(p => ({
							...p,
							website: tf('ERRORS.DEVANAGARI_NOT_ALLOWED')
						}));
					}}
				/>
			</Field>
		</div>
	);

	// ─── Step 4 — Finish ──────────────────────────────────────────────────────
	const renderStep4 = () => (
		<div className="flex flex-col gap-5">
			<div>
				<h3 className="font-custom-extra-bold text-base text-gray-900">{tf('STEP_4.TITLE')}</h3>
				<p className="font-custom-regular text-[0.75rem] text-gray-500 mt-0.5">{tf('STEP_4.SUBTITLE')}</p>
			</div>

			<Field error={errors.tagline} counter={`${formData.tagline.length}/80`}>
				<InputLabel htmlFor="tagline">
					{tf('STEP_4.TAGLINE.LABEL')} <span
					className="font-custom-regular text-gray-400">{tf('STEP_4.TAGLINE.OPTIONAL')}</span>
				</InputLabel>
				<p className="font-custom-regular text-[0.62rem] text-amber-600 -mt-0.5">
					(Please enter in English only &nbsp;/&nbsp; कृपया इंग्रजीत लिहा)
				</p>
				<input
					id="tagline" className={inputCls(false)}
					placeholder={tf('STEP_4.TAGLINE.PLACEHOLDER')}
					value={formData.tagline} maxLength={80}
					onChange={e => updateFormData('tagline', e.target.value)}
					onBlur={() => {
						if (containsDevanagari(formData.tagline))
							setErrors(p => ({...p, tagline: tf('ERRORS.DEVANAGARI_NOT_ALLOWED')}));
					}}
				/>
			</Field>

			<div className="flex flex-col gap-2">
				<InputLabel required>{tf('STEP_4.PRIMARY_CTA.LABEL')}</InputLabel>
				<p className="font-custom-regular text-[0.65rem] text-gray-400 -mt-0.5">
					{tf('STEP_4.PRIMARY_CTA.HINT')}
				</p>
				<div className="flex flex-col gap-2">
					{[
						{
							val: 'whatsapp', icon: MessageCircle,
							labelKey: 'STEP_4.PRIMARY_CTA.WHATSAPP',
							available: !!formData.phone.number,
							on: {background: '#DCFCE7', border: '2px solid #86EFAC', color: '#15803D'},
						},
						{
							val: 'call', icon: Phone,
							labelKey: 'STEP_4.PRIMARY_CTA.CALL',
							available: !!formData.callingPhone.number || formData.sameNumberForCalling,
							on: {background: '#EFF6FF', border: '2px solid #93C5FD', color: '#1D4ED8'},
						},
						{
							val: 'website', icon: Globe,
							labelKey: 'STEP_4.PRIMARY_CTA.WEBSITE',
							available: !!formData.website,
							on: {background: '#F5F3FF', border: '2px solid #DDD6FE', color: '#6D28D9'},
						},
					].map(({val, icon: Icon, labelKey, available, on}) => {
						const active = formData.primaryCta === val;
						return (
							// ── Wrapper div needed for tooltip positioning ──
							<div key={val} className="relative">
								<button
									type="button"
									onClick={() => {
										if (!available) {
											showDisabledCtaTooltip(val);
											return;
										}
										updateFormData('primaryCta', val);
									}}
									className="flex items-center gap-3 p-3 rounded-xl text-left w-full transition-all duration-200"
									style={active
										? {...on, boxShadow: '0 2px 8px rgba(0,0,0,0.08)'}
										: {
											background: available ? '#FAFAFA' : '#F9FAFB',
											border: '2px solid #E5E7EB',
											color: available ? '#6B7280' : '#D1D5DB',
											cursor: available ? 'pointer' : 'not-allowed',
										}}>
									<Icon className="w-4 h-4 shrink-0"/>
									<div className="flex-1 min-w-0">
										<p className="font-custom-bold text-sm">{tf(labelKey)}</p>
									</div>
									<div
										className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-all duration-200"
										style={{
											background: active ? 'currentColor' : '#F3F4F6',
											border: `2px solid ${active ? 'currentColor' : '#E5E7EB'}`
										}}>
										{active && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3}/>}
									</div>
								</button>

								{/* Disabled tooltip — only shown when user clicks a disabled option */}
								<div
									className={`absolute bottom-[calc(100%+8px)] left-0 w-44 transition-all duration-300 pointer-events-none z-50
                                        ${disabledCtaTooltip === val ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-1 scale-95'}`}>
									<div
										className="bg-slate-800 text-white text-[0.62rem] font-custom-medium px-3 py-2 rounded-lg shadow-lg leading-snug text-center">
										{tf('STEP_4.PRIMARY_CTA.DISABLED_TOOLTIP')}
									</div>
									<div className="absolute -bottom-1 left-6 w-2.5 h-2.5 bg-slate-800 rotate-45"/>
								</div>
							</div>
						);
					})}
				</div>
			</div>

			{/* ④ Terms checkbox — role + keyboard accessible */}
			<div role="checkbox" tabIndex={0} aria-checked={formData.termsAccepted}
			     className="flex gap-3 items-start p-4 rounded-xl cursor-pointer transition-all duration-200"
			     style={{
				     background: formData.termsAccepted ? '#FDF5F3' : '#FAFAFA',
				     border: `1.5px solid ${errors.termsAccepted ? '#FCA5A5' : formData.termsAccepted ? '#F5CEC7' : '#E5E7EB'}`,
			     }}
			     onClick={() => updateFormData('termsAccepted', !formData.termsAccepted)}
			     onKeyDown={e => {
				     if (e.key === ' ' || e.key === 'Enter') {
					     e.preventDefault();
					     updateFormData('termsAccepted', !formData.termsAccepted);
				     }
			     }}>
				<div
					className="w-5 h-5 rounded-md flex items-center justify-center shrink-0 mt-0.5 transition-all duration-200"
					style={{
						background: formData.termsAccepted ? '#D26A53' : '#fff',
						border: `2px solid ${formData.termsAccepted ? '#D26A53' : '#D1D5DB'}`
					}}>
					{formData.termsAccepted && <Check className="w-3 h-3 text-white" strokeWidth={3}/>}
				</div>
				<p className="font-custom-regular text-[0.72rem] text-gray-600 leading-relaxed select-none">
					{tf('STEP_4.TERMS.TEXT_PREFIX')} <span className="font-custom-bold text-gray-800">
						{formData.entityType === 'service' ? tf('STEP_4.TERMS.ENTITY_SERVICE') : tf('STEP_4.TERMS.ENTITY_BUSINESS')}
					</span> {tf('STEP_4.TERMS.TEXT_SUFFIX')}
				</p>
			</div>
			{errors.termsAccepted && (
				<p className="custom-input-error-message flex items-center gap-1 -mt-3">
					<span className="w-1.5 h-1.5 bg-red-400 rounded-full shrink-0"/> {errors.termsAccepted}
				</p>
			)}
		</div>
	);

	// ─── Render ───────────────────────────────────────────────────────────────
	// ① Animation class based on direction + current step key
	const stepAnimClass = stepDirectionRef.current === 1 ? 'step-forward' : 'step-backward';

	return (
		<div
			className="form-modal-container bg-white w-full lg:w-[60rem] flex flex-col overflow-hidden relative"
			style={{boxShadow: '0 32px 64px rgba(0,0,0,0.18)'}}
		>

			{/* Header */}
			<div className="shrink-0 px-6 pt-5 pb-4 bg-white z-20" style={{borderBottom: '1px solid #F3F3F3'}}>
				<div className="flex items-start justify-between">
					<div>
						<div className="flex items-center gap-2 mb-0.5">
							<div className="w-2 h-2 rounded-full" style={{background: '#D26A53'}}/>
							<p className="font-custom-bold text-[0.65rem] text-gray-400 uppercase tracking-widest">{tf('HEADER.SECTION_LABEL')}</p>
						</div>
						<h2 className="font-custom-extra-bold text-lg text-gray-900 tracking-tight">{tf('HEADER.TITLE')}</h2>
					</div>
					<button onClick={() => setIsYellowPageOnboardingFormModalOpen(false)}
					        className="w-8 h-8 flex items-center justify-center rounded-full transition-colors hover:bg-gray-100"
					        style={{border: '1.5px solid #E5E7EB'}} aria-label={tf('HEADER.CLOSE_ARIA')}>
						<X className="w-4 h-4 text-gray-500"/>
					</button>
				</div>
				<StepIndicator currentStep={currentStep}/>
			</div>

			{/* Body */}
			<div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-0">
				{/* Mobile preview */}
				<div className="md:hidden shrink-0 px-4 pt-4 pb-3 overflow-y-auto"
				     style={{background: '#F9F9F9', borderBottom: '1px solid #EFEFEF'}}>
					<PreviewCard
						formData={formData} ownerName={ownerName} nativePlace={nativePlace}
						ownerImageUrl={ownerImageUrl} ownerImageUrlBg={ownerImageUrlBg}
						updatedFields={updatedFields}
					/>
				</div>

				{/* ① Step content with direction-aware slide animation */}
				<div key={currentStep} className={`flex-1 md:w-[56%] overflow-y-auto px-6 py-5 ${stepAnimClass}`}>
					{currentStep === 1 && renderStep1()}
					{currentStep === 2 && renderStep2()}
					{currentStep === 3 && renderStep3()}
					{currentStep === 4 && renderStep4()}
				</div>

				{/* Desktop preview */}
				<div className="hidden md:flex md:w-[44%] overflow-y-auto flex-col justify-center px-6 py-6 shrink-0"
				     style={{background: '#F7F7F8', borderLeft: '1px solid #EDEDEE'}}>
					<div className="sticky top-6">
						<PreviewCard
							formData={formData} ownerName={ownerName} nativePlace={nativePlace}
							ownerImageUrl={ownerImageUrl} ownerImageUrlBg={ownerImageUrlBg}
							updatedFields={updatedFields}
						/>
					</div>
				</div>
			</div>

			{/* Footer */}
			<div className="shrink-0 px-6 py-4 bg-white z-20" style={{borderTop: '1px solid #F3F3F3'}}>
				<div className="flex items-center justify-between gap-3">
					{currentStep > 1 ? (
						<button onClick={prevStep}
						        className="px-5 py-2.5 rounded-xl font-custom-bold text-sm text-gray-600 transition-all duration-150 hover:bg-gray-100"
						        style={{border: '1.5px solid #E5E7EB'}}>
							← {tf('FOOTER.BACK')}
						</button>
					) : (
						<button onClick={() => setIsYellowPageOnboardingFormModalOpen(false)}
						        className="px-5 py-2.5 rounded-xl font-custom-bold text-sm text-gray-400 transition-all duration-150 hover:bg-gray-50"
						        style={{border: '1.5px solid #E5E7EB'}}>
							{tf('FOOTER.CANCEL')}
						</button>
					)}

					{/* Progress dots */}
					<div className="flex items-center gap-1.5">
						{[1, 2, 3, 4].map(s => (
							<div key={s} className="rounded-full transition-all duration-300"
							     style={{
								     width: currentStep === s ? '20px' : '6px',
								     height: '6px',
								     background: currentStep === s ? '#D26A53' : currentStep > s ? '#F5CEC7' : '#E5E7EB',
							     }}/>
						))}
					</div>

					{currentStep < 4 ? (
						<button onClick={nextStep}
						        className="flex items-center gap-2 px-6 py-2.5 rounded-xl transform-gpu font-custom-bold text-sm text-white transition-all duration-150 hover:opacity-90 active:scale-95"
						        style={{
							        background: 'linear-gradient(135deg, #D26A53, #C05A43)',
							        boxShadow: '0 4px 14px rgba(210,106,83,0.35)'
						        }}>
							{tf('FOOTER.NEXT')} <ArrowRight className="w-3.5 h-3.5"/>
						</button>
					) : (
						<button onClick={handleSubmit}
						        className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-custom-bold text-sm text-white transition-all duration-150 hover:opacity-90 active:scale-95"
						        style={{
							        background: 'linear-gradient(135deg, #5F0F40, #7B1454)',
							        boxShadow: '0 4px 14px rgba(95,15,64,0.35)'
						        }}>
							{tf('FOOTER.SUBMIT')}
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default YellowPageOnboardingForm;